import { CascaderValue } from "element-plus";
import { Ref } from "vue";
import {
  FormElementType,
  FileUploadAnswerValue,
  MailingAddressAnswerValue,
  FormResponseAnswer,
  FormElementBase,
  FormResponse,
  NameStampAnswerValue,
  LinkedAnswerAnswerValue,
  RepeaterHostAnswerValue,
  DiscussionAnswerValue,
  ActivityDateAnswerValue,
} from "~/api";

interface FormElementAnswerTypes {
  [FormElementType.ACTION_BUTTON]: null;
  [FormElementType.ANSWER_REVIEW]: number; // number of unanswered questions
  [FormElementType.BOOLEAN]: boolean;
  [FormElementType.CASCADER]: CascaderValue;
  [FormElementType.CHANGE_STATUS_BUTTON]: null;
  [FormElementType.CHECKBOXES]: string[];
  [FormElementType.DATE]: string;
  [FormElementType.DIVIDER]: null;
  [FormElementType.DISCUSSION]: DiscussionAnswerValue;
  [FormElementType.ENTITY]: null;
  [FormElementType.FILE_UPLOAD]: FileUploadAnswerValue[];
  [FormElementType.FORM_TEXT]: string;
  [FormElementType.HEADING]: null;
  [FormElementType.MAILING_ADDRESS]: MailingAddressAnswerValue;
  [FormElementType.MONEY]: number;
  [FormElementType.NAME_STAMP]: NameStampAnswerValue[];
  [FormElementType.NUMBER]: number;
  [FormElementType.RADIO_BUTTONS]: string;
  [FormElementType.REPEATER]: RepeaterHostAnswerValue;
  [FormElementType.RICH_TEXT]: string;
  [FormElementType.SECTION]: null;
  [FormElementType.SECURITY_GROUP]: null;
  [FormElementType.SELECT_LIST]: string;
  [FormElementType.SIMPLE_TEXT]: string;
  [FormElementType.USER_SELECT]: string;
  [FormElementType.LINKED_ANSWER]: LinkedAnswerAnswerValue; // this one will change to accomodate more cases over time

  [FormElementType.APPLICATION_STATUS_SELECT]: string;

  // TODO: what is the answer type for a compliance item?
  // we need an answer for the answer review element to work correctly
  [FormElementType.COMPLIANCE_ITEM]: string;
  [FormElementType.WORKFLOW_ASSOCIATION]: string[];
  [FormElementType.ACTIVITY_DATE]: ActivityDateAnswerValue;
}
export type AnswerValue<TAnswerType extends FormElementType> =
  FormElementAnswerTypes[TAnswerType];

export type FormAnswerPlus<TAnswerType extends FormElementType> = {
  formElementGuid: FormResponseAnswer["formElementGuid"];
  answerValue: AnswerValue<TAnswerType>;
};

export interface FormResponseAnswerMap {
  [key: string]: {
    formElementGuid: FormResponseAnswer["formElementGuid"];
    answerValue: AnswerValue<keyof FormElementAnswerTypes>;
    isHidden?: boolean;
  };
}

export type UpdateAnswerApiResult<TAnswerType extends FormElementType> = {
  status: "updated" | "deleted" | "error";
  value: AnswerValue<TAnswerType> | null;
};
export type UpdateAnswerApiFunc<TAnswerType extends FormElementType> = (
  newVal: AnswerValue<TAnswerType> | null,
  element: FormElementBase,
  formResponse: FormResponse,
) => Promise<UpdateAnswerApiResult<TAnswerType>>;

export type UseAnswer = <TAnswerType extends FormElementType>(
  /** The element to get the answer ref for */
  element: FormElementBase,
  /** A custom hook that calls the API to update the value */
  apiUpdateFunc?: UpdateAnswerApiFunc<TAnswerType>,
) => Ref<AnswerValue<TAnswerType> | undefined>;

// TODO: is there a better way to do this?
export enum FormElementMappedToNumberType {
  Boolean = 0,
  Checkboxes = 1,
  Date = 2,
  FileUpload = 3,
  AnswerReview = 4,
  FormText = 5,
  MailingAddress = 6,
  Money = 7,
  Number = 8,
  Heading = 9,
  RadioButtons = 10,
  Repeater = 11,
  RichText = 12,
  Section = 13,
  SelectList = 14,
  SimpleText = 15,
  ChangeStatusButton = 16,
  NameStamp = 17,
  LinkedAnswer = 18,
  ComplianceItem = 19,
  Divider = 20,
  Entity = 21,
  UserSelect = 22,
  WorkflowAssociation = 23,
  ActivityDate = 24,
}
